import type {
  CustomGraph,
  CustomNode,
  CustomNodeType,
} from '@/types/custom-playbooks';

/** Frozen API play_record_errors.RETRY_STEP_TYPES. */
const RETRY_STEPS: ReadonlySet<CustomNodeType> = new Set([
  'enrich',
  'research',
  'validate',
  'identify_person',
  'crm_action',
  'notify',
  'find_people',
  'get_company',
  'find_open_jobs',
  'find_reviews',
  'run_play',
  'add_to_sequence',
  'export',
  'webhook',
  'job_history',
  'count_people',
  'instantly_capacity',
]);
export const supportsRetries = (type: CustomNodeType) => RETRY_STEPS.has(type);

/**
 * Frozen API play_record_errors.DEFAULT_RETRIES: what a step with no error
 * setting at all retries on an agent play, the same as the steps Clu builds.
 * Only a step set to 0 has chosen not to retry.
 */
const DEFAULT_RETRIES = 3;

/** A step's error connection (G29) carries this arm key. */
export const ERROR_ARM = 'error';
export type OnErrorThen = 'stop' | 'continue' | 'route';

/** Frozen API custom_playbook_graph.ON_ERROR_STEP_TYPES: Notify retries only. */
const ERROR_PATH_STEPS: ReadonlySet<CustomNodeType> = new Set([
  'enrich',
  'research',
  'validate',
  'identify_person',
  'crm_action',
  'find_people',
  'get_company',
  'find_open_jobs',
  'find_reviews',
  'run_play',
  'add_to_sequence',
  'export',
  'webhook',
  'job_history',
  'count_people',
]);
export const supportsErrorPath = (type: CustomNodeType) =>
  ERROR_PATH_STEPS.has(type);
export const onErrorThen = (node: Pick<CustomNode, 'onError'>): OnErrorThen => {
  const then = node.onError?.then;
  return then === 'continue' || then === 'route' ? then : 'stop';
};

/** The validator's G29 findings for one step (API mirror). */
export function errorPathIssues(
  graph: CustomGraph,
  node: CustomNode,
  agentPlay: boolean
): { nodeId: string; code: string; message: string }[] {
  const issues: { nodeId: string; code: string; message: string }[] = [];
  const then = node.onError?.then;
  if (then !== undefined && !['stop', 'continue', 'route'].includes(then)) {
    issues.push({
      nodeId: node.id,
      code: 'on_error_then',
      message: 'If this step fails: stop, continue or route',
    });
  } else if (then && then !== 'stop' && !supportsErrorPath(node.type)) {
    issues.push({
      nodeId: node.id,
      code: 'on_error_step',
      message: "This step can't take an error setting",
    });
  } else if (then && then !== 'stop' && !agentPlay) {
    issues.push({
      nodeId: node.id,
      code: 'agent_play_only',
      message: 'Error paths run only on an agent play.',
    });
  }
  if (node.type === 'branch' || node.type === 'approval') return issues;
  const routes = then === 'route' && supportsErrorPath(node.type);
  const hasEdge = graph.edges.some(
    (edge) => edge.source === node.id && edge.branchKey === ERROR_ARM
  );
  if (routes && !hasEdge)
    issues.push({
      nodeId: node.id,
      code: 'error_edge_missing',
      message: 'Connect the error path',
    });
  else if (hasEdge && !routes)
    issues.push({
      nodeId: node.id,
      code: 'error_edge_unused',
      message:
        'This step stops on errors; remove the error path or send errors down it',
    });
  return issues;
}

/**
 * The step with its failure path set. A step with no retries of its own never
 * gets one written here: the server treats a step with no error setting as
 * the default (which keeps a send that may have landed out of the retries) and
 * an explicit 3 as chosen. Back to Stop, with nothing else set, the step has
 * no error setting again, so it is on the default as it was.
 */
function withOnErrorThen(node: CustomNode, then: OnErrorThen): CustomNode {
  const settings = node.onError;
  // null is an explicit no retries (``retryCount`` reads it as 0): keep it so,
  // never the default three.
  if (settings === null)
    return then === 'stop' ? node : { ...node, onError: { retries: 0, then } };
  const bare = !settings || (!('retries' in settings) && 'then' in settings);
  if (then === 'stop' && bare) {
    const rest = { ...node };
    delete rest.onError;
    return rest;
  }
  return { ...node, onError: { ...settings, then } };
}

/**
 * Set what a step does with failed records. Leaving Error path drops the
 * error connection and the steps only it leads to.
 */
export function setNodeOnErrorThen(
  graph: CustomGraph,
  id: string,
  then: OnErrorThen
): CustomGraph {
  const node = graph.nodes.find((n) => n.id === id);
  if (!node || !supportsErrorPath(node.type)) return graph;
  const nodes = graph.nodes.map((n) =>
    n.id === id ? withOnErrorThen(n, then) : n
  );
  let next: CustomGraph = { ...graph, nodes };
  const error = graph.edges.find(
    (edge) => edge.source === id && edge.branchKey === ERROR_ARM
  );
  if (then !== 'route' && error) {
    next = { ...next, edges: next.edges.filter((e) => e.id !== error.id) };
    if (!next.edges.some((e) => e.target === error.target)) {
      const doomed = new Set([error.target]);
      let changed = true;
      while (changed) {
        changed = false;
        for (const n of next.nodes) {
          if (doomed.has(n.id)) continue;
          const parents = next.edges.filter((e) => e.target === n.id);
          if (parents.length && parents.every((e) => doomed.has(e.source))) {
            doomed.add(n.id);
            changed = true;
          }
        }
      }
      next = {
        ...next,
        nodes: next.nodes.filter((n) => !doomed.has(n.id)),
        edges: next.edges.filter(
          (e) => !doomed.has(e.source) && !doomed.has(e.target)
        ),
      };
    }
  }
  return next;
}
/**
 * How many times a step retries its temporary failures on an agent play: its
 * own setting, else the default for a step that supports retries and has no
 * setting (the server does the same, so the canvas shows what will happen).
 */
export const retryCount = (node: Pick<CustomNode, 'onError' | 'type'>) => {
  if (
    node.onError === undefined ||
    (node.onError !== null &&
      typeof node.onError === 'object' &&
      !Array.isArray(node.onError) &&
      !Object.prototype.hasOwnProperty.call(node.onError, 'retries'))
  )
    return supportsRetries(node.type) ? DEFAULT_RETRIES : 0;
  const count = node.onError?.retries;
  return Number.isInteger(count) && count! >= 0 && count! <= 3 ? count! : 0;
};

function parkedAt(
  error: Record<string, unknown> | null | undefined,
  code: string
): string | null {
  if (error?.code !== code || typeof error.resumeAt !== 'string') return null;
  const value = error.resumeAt;
  const time = new Date(
    /(?:Z|[+-]\d{2}:\d{2})$/.test(value) ? value : `${value}Z`
  );
  return Number.isNaN(time.getTime())
    ? null
    : time.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}

export function setNodeRetries(
  graph: CustomGraph,
  id: string,
  retries: number
): CustomGraph {
  if (!Number.isInteger(retries) || retries < 0 || retries > 3) return graph;
  return {
    ...graph,
    nodes: graph.nodes.map((node) =>
      node.id === id && supportsRetries(node.type)
        ? { ...node, onError: { ...node.onError, retries } }
        : node
    ),
  };
}

export const retryAt = (error?: Record<string, unknown> | null) =>
  parkedAt(error, 'retrying');
export const waitAt = (error?: Record<string, unknown> | null) =>
  parkedAt(error, 'waiting');

/** "waiting · next Oct 4": the next resume of this run's waiting records. */
export function waitingNowLabel(next: string | null | undefined): string {
  if (!next) return 'waiting';
  const date = new Date(
    /(?:Z|[+-]\d{2}:\d{2})$/.test(next) ? next : `${next}Z`
  );
  return Number.isNaN(date.getTime())
    ? 'waiting'
    : `waiting · next ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}
